import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  InputLabel,
  TextareaAutosize,
  Grid,
  Checkbox,
  Chip,
  Avatar,
} from "@mui/material";
import { Formik, Form, FormikHelpers, Field } from "formik";
import * as Yup from "yup";
import ToastManager from "../../Components/ToastManager";
import { makeStyles } from "../../utils/makeStyles";
import DeleteIcon from "@mui/icons-material/Delete";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import tz from "dayjs/plugin/timezone";
import localizedFormat from "dayjs/plugin/localizedFormat";
import { NetworkManager } from "../../Components/NetworkManager";
import { CommonUtil, readJson } from "../../utils/CommonUtil";
import {
  buildBaseTemplateData,
  buildPayload,
} from "../../utils/payloadBuilder";
import EditIcon from "@mui/icons-material/Edit";
import AudienceSelector from "../BroadcastFBManage/DMComponents/AudienceSelector";
import ImageUpload from "../BroadcastFBManage/DMComponents/ImageUpload";
import ExclusionCampaignSelector, {
  PastCampaign,
} from "../BroadcastFBManage/DMComponents/ExclusionCampaignSelector";
import MessageConfirmDialog, {
  type ConfirmationData,
} from "../BroadcastFBManage/DMComponents/MessageConfirmDialog";

dayjs.extend(utc);
dayjs.extend(tz);
dayjs.extend(localizedFormat);

export interface Message {
  id: number;
  templateName: string;
  image: string;
  imageUrl: string;
  scheduledTime: any;
  headline: string;
  messagetext: string;
  buttontext: string;
  buttonurl: string;
  fileName: string;
  buttonChecked: boolean;
}

interface MessageDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (newMessage: Message) => void;
  editMessage: any;
  fetchMarketingMessages: () => void;
}

interface MessageFormValues {
  templateName: string;
  image: string;
  imageUrl: string;
  fileName: string;
  scheduledTime: string;
  headline: string;
  messagetext: string;
  buttontext: string;
  buttonurl: string;
  buttonChecked: boolean;
}

const useStyles = makeStyles()({
  selectMenu: {
    maxHeight: "160px",
  },
  checkBoxStyles: {
    display: "flex",
    flexWrap: "wrap",
    gap: 0.5,
    marginTop: "1.5vh",
  },
  fieldStyle: {
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        maxHeight: "60px",
      },
    },
  },
});

export const NewMessageDialog: React.FC<MessageDialogProps> = ({
  open,
  onClose,
  onSave,
  editMessage,
  fetchMarketingMessages,
}) => {
  const [multiButton, setMultiButton] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [selected, setSelected] = useState<any[]>([]);
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(null);
  const [image, setImage] = useState<string>("");
  const [imageUrl, setImageUrl] = useState<string>("");
  const [imageUrlLink, setImageUrlLink] = useState<any>("");
  const [fileName, setFileName] = useState<string>("No Image chosen");
  const [imageUploading, setImageUploading] = useState(false);
  const [audienceLoading] = useState(false);
  const [opendrop, setOpenDrop] = useState<boolean>(false);
  const [audiences, setAudiences] = useState<any[]>([]);
  const [selectedAudiences, setSelectedAudiences] = useState<any[]>([]);
  const [pastCampaigns, setPastCampaigns] = useState<PastCampaign[]>([]);
  const [excludedCampaigns, setExcludedCampaigns] = useState<PastCampaign[]>(
    [],
  );
  const [exclusionDropOpen, setExclusionDropOpen] = useState(false);
  const [exclusionLoading, setExclusionLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmData, setConfirmData] = useState<ConfirmationData | null>(null);
  const [confirmError, setConfirmError] = useState(false);
  const [buttonCheck, setButtonCheck] = useState(false);
  const [genericFields, setGenericFields] = useState(true);
  const [updateText, setUpdateText] = useState(true);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const [initialValues, setInitialValues] = useState<MessageFormValues>({
    templateName: "",
    image: "",
    imageUrl: "",
    fileName: "",
    scheduledTime: "",
    headline: "",
    messagetext: "",
    buttontext: "",
    buttonurl: "",
    buttonChecked: false,
  });
  const validationSchema = Yup.object().shape({});
  const { classes } = useStyles();

  useEffect(() => {
    if (open && editMessage) {
      fetchAudiences();
      fetchPastCampaigns();
    }
  }, [open, editMessage]);
  useEffect(() => {
    if (!open || !editMessage) return;

    const estTime = dayjs(editMessage.scheduledTime)
      .utc()
      .tz("America/New_York");

    setSelectedDate(estTime);
  }, [open, editMessage]);

  useEffect(() => {
    if (!open) return;
    if (!editMessage) {
      setInitialValues({
        templateName: "",
        image: "",
        imageUrl: "",
        fileName: "",
        scheduledTime: "",
        headline: "",
        messagetext: "",
        buttontext: "",
        buttonurl: "",
        buttonChecked: false,
      });
      setSelectedDate(null);
      setSelectedAudiences([]);
      setExcludedCampaigns([]);
      setPastCampaigns([]);
      setLinkUrl("");
      setMultiButton("");
      setSelected([]);
      setFileName("No Image chosen");
      setImageUrl("");
      setImageUrlLink("");
      setButtonCheck(false);
      setGenericFields(true);
      setUpdateText(true);
      setEditingIndex(null);
      return;
    }
    const template = editMessage?.templateDetails;
    const card = template.card?.[1] || template.card?.[0] || {};
    const flow = template.flows?.[0] || {};
    setImageUrl(card?.imageUrl);
    setImageUrlLink(card?.imageUrl);
    setFileName(card.fileName || "No Image chosen");
    setMultiButton("");
    setLinkUrl("");
    setSelected(
      template.flows?.map((btn: any) => ({
        multiButton: btn.responseText,
        buttonType: btn.responseSubType || "weburl",
        linkUrl: btn.responseUrlLink,
      })) || [],
    );
    setButtonCheck(card?.mediaChecked);
    setGenericFields(card?.mediaChecked ? false : true);
    setInitialValues({
      templateName: template.templateName || "",
      image: card.imageUrl || "",
      imageUrl: card.imageUrl || "",
      fileName: card.fileName || "",
      scheduledTime: editMessage.scheduledTime || "",
      headline: card.genericTitle || "",
      messagetext: card.genericSubTitle || card.textMessage,
      buttontext: flow.responseText || "",
      buttonurl: flow.responseUrlLink || "",
      buttonChecked: card.mediaChecked,
    });
  }, [open, editMessage]);

  useEffect(() => {
    if (!audiences.length || !editMessage) return;
    const existing = editMessage.audienceList || {};
    const messengerOptin = (existing.MESSENGER_OPTIN || []).map(
      (name: string) => ({ title: name, type: "MESSENGER_OPTIN" as const }),
    );
    const customAudiences = (existing.CUSTOM_AUDIENCE || []).map(
      (id: string) =>
        audiences.find((x) => x.id === id) || {
          title: id,
          id,
          type: "CUSTOM_AUDIENCE" as const,
        },
    );
    setSelectedAudiences([...messengerOptin, ...customAudiences]);
  }, [audiences, editMessage]);

  useEffect(() => {
    if (!pastCampaigns.length || !editMessage) return;
    const existingIds: string[] = editMessage.excludedCampaignIds || [];
    setExcludedCampaigns(
      existingIds.map(
        (id) =>
          pastCampaigns.find((x) => x.id === id) || {
            id,
            name: id,
            scheduledTime: "",
          },
      ),
    );
  }, [pastCampaigns, editMessage]);

  const onClickMultiAddButton = () => {
    if (!multiButton.trim() || !linkUrl.trim()) {
      ToastManager.showToast(
        "Please enter both Button Text and Link URL!",
        "Error",
      );
      return;
    }
    if (editingIndex !== null) {
      setSelected((prev) =>
        prev.map((item, i) =>
          i === editingIndex
            ? { multiButton, buttonType: "weburl", linkUrl }
            : item,
        ),
      );
      setEditingIndex(null);
      setUpdateText(true);
      setMultiButton("");
      setLinkUrl("");
      return;
    }
    if (selected.length >= 3) {
      ToastManager.showToast("You can add up to 3 buttons only!", "Error");
      return;
    }
    setSelected((prev) => [
      ...prev,
      { multiButton, buttonType: "weburl", linkUrl },
    ]);
    setMultiButton("");
    setLinkUrl("");
  };
  const {
    facebook: { pageId, systemUserId },
  } = readJson("fbbPageDetail") || "{}";
  const { adAccountId } = readJson("fbbAdAccountDetail") || "{}";
  const { messageTemplateId } = readJson("selectedMessage") || {};
  const { messageCampaignId } = readJson("selectedCampaign") || {};

  const fetchAudiences = () => {
    let params = `pageId=${pageId}&systemUserId=${systemUserId}&adAccountId=${adAccountId}`;

    NetworkManager.sendJsonRequest(
      "broadcastMessageTemplates/getAudiencesList",
      "",
      params,
      "GET",
      true,
      onSuccessGetAudiences,
      onErr,
    );
  };
  const onSuccessGetAudiences = (data: any) => {
    const respData = CommonUtil.parseData(data);
    if (respData.statusCode === "BM0000") {
      const audienceList = respData?.audienceList;
      const customAudiences =
        audienceList?.CUSTOM_AUDIENCE?.map((item: any) => ({
          title: item.customAudienceName,
          id: item.customAudienceId,
          type: "CUSTOM_AUDIENCE" as const,
        })) || [];

      const messengerOptins =
        audienceList?.MESSENGER_OPTIN?.map((name: string) => ({
          title: name,
          type: "MESSENGER_OPTIN" as const,
        })) || [];
      setAudiences([...customAudiences, ...messengerOptins]);
    }
  };
  const onErr = () => {};

  const fetchPastCampaigns = () => {
    if (!pageId || !adAccountId) return;
    setExclusionLoading(true);
    let params = `pageId=${pageId}&adAccountId=${adAccountId}`;
    if (messageCampaignId)
      params += `&currentMessageCampaignId=${messageCampaignId}`;
    if (editMessage && messageTemplateId)
      params += `&messageTemplateId=${messageTemplateId}`;
    NetworkManager.sendJsonRequest(
      "broadcastMessageTemplates/getCampaignsForExclusion",
      "",
      params,
      "GET",
      true,
      (data: any) => {
        const respData = CommonUtil.parseData(data);
        if (respData?.statusCode === "BM0000") {
          const list = respData.campaignForExclusionList || [];
          const campaigns = list.map((c: any) => ({
            id: c.messageCampaignId,
            name: c.messageCampaignName,
            scheduledTime: c.creationDate,
            recipientCount: c.deliveredOrNullCount,
          }));
          setPastCampaigns(campaigns);
        }
        setExclusionLoading(false);
      },
      () => setExclusionLoading(false),
    );
  };

  const handleFileChange = (e: any) => {
    const file = e.target.files[0];
    if (
      file &&
      (file.type === "image/png" ||
        file.type === "image/jpeg" ||
        file.type === "image/jpg")
    ) {
      setImage(file);
      setImageUrl(URL.createObjectURL(file));
      setFileName(file.name);
    } else {
      ToastManager.showToast("Only JPG and PNG files are allowed!", "Error");
      setFileName("No file chosen");
    }
  };
  const handleChangeAudiences = (_: any, newValue: any) => {
    const allOptionSelected = newValue.some(
      (a: any) => a.title === "All" && a.type === "MESSENGER_OPTIN",
    );
    if (allOptionSelected) {
      const filtered = newValue.filter(
        (a: any) => a.title === "All" || a.type === "CUSTOM_AUDIENCE",
      );
      setSelectedAudiences(filtered);
    } else {
      setSelectedAudiences(newValue);
    }
  };
  const onClickUpload = async () => {
    if (!image) {
      ToastManager.showToast(
        "Please select an image before uploading",
        "Error",
      );
      return;
    }
    if (!pageId) {
      ToastManager.showToast("Page ID is required", "Error");
      return;
    }
    setImageUploading(true);
    const formData = new FormData();
    formData.append("imageFileData", image);
    try {
      const baseUrl = NetworkManager.serverPath;
      const lToken = localStorage.getItem("cognitoAccessToken");
      const response = await fetch(
        `${baseUrl}broadcastMessageTemplates/uploadImage?pageId=${pageId}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${lToken}`,
          },
          body: formData,
        },
      );
      const data = await response.json();
      if (data.statusCode === "BM0000") {
        setImageUrl(data.imageUrl);
        ToastManager.showToast("Image Uploaded Successfully", "Success");
      } else {
        ToastManager.showToast("Upload failed", "Error");
      }
    } catch (error) {
      ToastManager.showToast("Internal server error", "Error");
    } finally {
      setImageUploading(false);
    }
  };

  const fetchConfirmationData = () => {
    const messengerOptins = selectedAudiences
      .filter((a) => a.type === "MESSENGER_OPTIN")
      .map((a) => a.title);
    const customAudiences = selectedAudiences
      .filter((a) => a.type === "CUSTOM_AUDIENCE")
      .map((a) => ({ customAudienceName: a.title, customAudienceId: a.id }));
    const excludedCampaignsList = excludedCampaigns.map((c) => ({
      messageCampaignId: c.id,
      messageCampaignName: c.name,
    }));

    const params = `pageId=${pageId}`;
    const payload = {
      audienceList: {
        CUSTOM_AUDIENCE: customAudiences,
        MESSENGER_OPTIN: messengerOptins,
      },
      excludedCampaigns: excludedCampaignsList,
    };

    NetworkManager.sendJsonRequest(
      "broadcastMessageTemplates/getPreSendConfirmationDetails",
      payload,
      params,
      "POST",
      true,
      (data: any) => {
        const respData = CommonUtil.parseData(data);
        if (respData?.statusCode === "BM0000") {
          setConfirmData(respData);
        } else {
          setConfirmError(true);
        }
      },
      () => setConfirmError(true),
    );
  };

  const validateBeforeSend = (values: MessageFormValues): boolean => {
    const messengerOptins = selectedAudiences
      .filter((a) => a.type === "MESSENGER_OPTIN")
      .map((a) => a.title);
    const customAudiences = selectedAudiences
      .filter((a) => a.type === "CUSTOM_AUDIENCE")
      .map((a) => a.id);

    if (!buttonCheck && !imageUrl && !values.image && !imageUrlLink) {
      ToastManager.showToast(
        "Please upload an image or provide an image URL before saving!",
        "Error",
      );
      return false;
    }
    if (!selectedDate) {
      ToastManager.showToast("Please select a scheduled date and time!", "Error");
      return false;
    }
    if (messengerOptins.length === 0 && customAudiences.length === 0) {
      ToastManager.showToast("Please select at least one audience!", "Error");
      return false;
    }
    if (selected.length === 0) {
      ToastManager.showToast(
        "Please add at least one button for Button Template!",
        "Error",
      );
      return false;
    }
    if (linkUrl.length !== 0 || multiButton.length !== 0) {
      ToastManager.showToast(
        "Please add the button or clear the Button Text and Link URL fields!",
        "Error",
      );
      return false;
    }
    return true;
  };

  const handleSubmit = (
    values: MessageFormValues,
    formikHelpers: FormikHelpers<MessageFormValues>,
    onSave: (message: Message) => void,
    onClose: () => void,
  ) => {
    const messengerOptins = selectedAudiences
      .filter((a) => a.type === "MESSENGER_OPTIN")
      .map((a) => a.title);

    const customAudiences = selectedAudiences
      .filter((a) => a.type === "CUSTOM_AUDIENCE")
      .map((a) => a.id);

    const audienceList = {
      MESSENGER_OPTIN: messengerOptins,
      CUSTOM_AUDIENCE: customAudiences,
    };

    const excludedCampaignIds = excludedCampaigns.map((c) => c.id);

    const finalScheduledTime = selectedDate
      ? selectedDate.utc().format("YYYY-MM-DD HH:mm:ss")
      : null;

    if (!buttonCheck) {
      if (!imageUrl && !values.image && !imageUrlLink) {
        ToastManager.showToast(
          "Please upload an image or provide an image URL before saving!",
          "Error",
        );
        return;
      }
    }
    if (!selectedDate) {
      ToastManager.showToast(
        "Please select a scheduled date and time!",
        "Error",
      );
      return;
    }
    if (messengerOptins.length === 0 && customAudiences.length === 0) {
      ToastManager.showToast("Please select at least one audience!", "Error");
      return;
    }

    if (selected.length === 0) {
      ToastManager.showToast(
        "Please add at least one button for Button Template!",
        "Error",
      );
      return;
    }

    if (linkUrl.length !== 0 || multiButton.length !== 0) {
      ToastManager.showToast(
        "Please add the button or clear the Button Text and Link URL fields!",
        "Error",
      );
      return;
    }

    const baseTemplateData = buildBaseTemplateData({
      values,
      selected,
      imageUrl,
      imageUrlLink,
      linkUrl,
      fileName,
      buttonCheck,
      pageId,
      editMessage,
    });
    const isEdit = !!editMessage;
    const payload = buildPayload({
      isEdit,
      audienceList,
      baseTemplateData,
      pageId,
      adAccountId,
      finalScheduledTime,
      messageTemplateId,
      messageCampaignId,
      templateName: values.templateName,
      excludedCampaignIds,
    });

    const url = isEdit
      ? "broadcastMessageTemplates/updateMessageTemplate"
      : "broadcastMessageTemplates/saveMessageTemplate";

    const params = isEdit
      ? `pageId=${pageId}&adAccountId=${adAccountId}&scheduledTime=${finalScheduledTime}&messageTemplateId=${messageTemplateId}&templateName=${values.templateName}`
      : `pageId=${pageId}&adAccountId=${adAccountId}&messageCampaignId=${messageCampaignId}&templateName=${values.templateName}&scheduledTime=${finalScheduledTime}`;

    try {
      NetworkManager.sendJsonRequest(
        url,
        payload,
        params,
        "POST",
        true,
        (data: any) => {
          const respData = CommonUtil.parseData(data);
          const filteredButtons = selected.filter((btn) =>
            btn.multiButton?.trim(),
          );
          const finalImageUrl = imageUrl || imageUrlLink || "";

          if (respData.statusCode === "BM0000") {
            const newMessage: any = {
              id: Date.now(),
              messageTemplateName: values.templateName,
              image: values.image || "",
              imageUrl: finalImageUrl,
              scheduledTime: finalScheduledTime,
              headline: values.headline || "",
              messagetext: values.messagetext,
              buttontext: values.buttontext,
              buttonurl: linkUrl,
              fileName: values.fileName,
              audienceList: payload.audienceList,
              buttons: filteredButtons.map((btn) => ({
                responseText: btn.multiButton,
                responseUrlLink: btn.linkUrl,
              })),
              buttonChecked: values.buttonChecked,
            };

            onSave(newMessage);
            formikHelpers.resetForm();
            ToastManager.showToast(
              isEdit
                ? "Message template updated successfully!"
                : "Message template saved successfully!",
              "Success",
            );
            onClose();
            fetchMarketingMessages();
          } else if (respData.statusCode === "BM0001") {
            ToastManager.showToast(
              "Name is already taken. Please choose another one.",
              "Error",
            );
          }
        },
        () => {
          ToastManager.showToast("Network error occurred.", "Error");
        },
      );
    } catch (err) {}
  };

  const handleEdit = (index: number, value: any) => {
    setEditingIndex(index);
    setLinkUrl(value.linkUrl);
    setMultiButton(value.multiButton);
    setUpdateText(false);
  };
  const handleDelete = (index: number) => {
    setSelected((prev) => prev.filter((_, i) => i !== index));
  };
  const handleAudienceOpen = () => {
    setOpenDrop(true);
    if (!editMessage || !audiences.length) {
      fetchAudiences();
    }
  };
  const handleExclusionOpen = () => {
    setExclusionDropOpen(true);
    if (!editMessage || !pastCampaigns.length) {
      fetchPastCampaigns();
    }
  };
  return (
    <>
      <Dialog
        open={open}
        onClose={(_, reason) => {
          if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
            onClose();
          }
        }}
        fullWidth
        slotProps={{
          paper: { sx: { maxHeight: "100%", maxWidth: "40%" } },
        }}>
        <DialogTitle sx={{ textAlign: "center" }}>
          {editMessage
            ? "Edit Message Template"
            : "Create & Schedule Message Template"}
        </DialogTitle>
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={validationSchema}
          onSubmit={(values, helpers) =>
            handleSubmit(values, helpers, onSave, onClose)
          }>
          {({ handleChange, values, setFieldValue, submitForm }) => (
            <Form>
              <DialogContent
                className="custom-scrollbar-css"
                sx={{
                  pt: 2,
                  maxHeight: "400px",
                  overflowY: "auto",
                }}>
                <Box>
                  <Box>
                    <TextField
                      fullWidth
                      name="templateName"
                      label="Template Name"
                      required
                      value={values.templateName}
                      onChange={handleChange}
                      sx={{ mt: 1 }}
                    />
                    <AudienceSelector
                      audiences={audiences}
                      selectedAudiences={selectedAudiences}
                      loading={audienceLoading}
                      openDropdown={opendrop}
                      onOpen={handleAudienceOpen}
                      onClose={() => setOpenDrop(false)}
                      onChange={(newValue) =>
                        handleChangeAudiences(null, newValue)
                      }
                    />
                    <ExclusionCampaignSelector
                      pastCampaigns={pastCampaigns}
                      selectedExclusions={excludedCampaigns}
                      loading={exclusionLoading}
                      openDropdown={exclusionDropOpen}
                      onOpen={handleExclusionOpen}
                      onClose={(_e, _r) => setExclusionDropOpen(false)}
                      onChange={(newValue) => setExcludedCampaigns(newValue)}
                    />
                    <Box className={classes.checkBoxStyles}>
                      <Field
                        as={Checkbox}
                        label="Button Template"
                        type="checkbox"
                        name="buttonChecked"
                        checked={buttonCheck}
                        onChange={(e: any) => {
                          setButtonCheck(e.target.checked);
                          setFieldValue("buttonChecked", e.target.checked);
                          if (e.target.checked) {
                            setGenericFields(false);
                          } else {
                            setGenericFields(true);
                          }
                        }}
                      />
                      <InputLabel sx={{ mt: "1.5vh" }}>
                        Button Template
                      </InputLabel>
                    </Box>
                    {genericFields ? (
                      <>
                        <ImageUpload
                          imageUrl={imageUrl}
                          imageUrlLink={imageUrlLink}
                          fileName={fileName}
                          loading={imageUploading}
                          onFileChange={handleFileChange}
                          onUpload={onClickUpload}
                          onDelete={() => {
                            setFileName("No Image chosen");
                            setImage("");
                            setImageUrl("");
                            setImageUrlLink("");
                          }}
                          onImageUrlChange={(url) => {
                            setImageUrlLink(url);
                            setFieldValue("imageUrl", url);
                          }}
                        />
                        <TextField
                          fullWidth
                          name="headline"
                          required
                          label="Image Headline"
                          value={values.headline}
                          onChange={handleChange}
                          margin="normal"
                        />{" "}
                      </>
                    ) : (
                      ""
                    )}
                    <Box sx={{ mt: 1 }}>
                      <InputLabel>Message Text:</InputLabel>
                      <TextareaAutosize
                        name="messagetext"
                        aria-label="minimum height"
                        required
                        value={values.messagetext}
                        minRows={4}
                        maxLength={buttonCheck ? 640 : undefined}
                        placeholder="message..."
                        style={{ width: "100%" }}
                        onChange={(e) => {
                          setFieldValue("messagetext", e.target.value);
                        }}
                      />
                      {buttonCheck ? (
                        <InputLabel sx={{ fontSize: "12px" }}>
                          MessageText must be less than 640 characters (
                          {values.messagetext.length}/640)
                        </InputLabel>
                      ) : (
                        ""
                      )}
                    </Box>
                    <Box sx={{ mt: 2 }}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DemoContainer components={["DateTimePicker"]}>
                          <DateTimePicker
                            label="Date and Time (EST)"
                            disablePast
                            timezone="America/New_York"
                            value={selectedDate ? dayjs(selectedDate) : null}
                            onChange={(newValue: any) => {
                              const newDate = newValue ? newValue : null;
                              if (newDate !== selectedDate) {
                                setSelectedDate(newDate);
                              }
                            }}
                          />
                        </DemoContainer>
                      </LocalizationProvider>
                    </Box>
                    <TextField
                      fullWidth
                      required={selected.length > 0 ? false : true}
                      name="buttonurl"
                      label="Button Link URL"
                      value={linkUrl}
                      onChange={(e) => {
                        setLinkUrl(e.target.value);
                        if (!e.target.value) {
                          setMultiButton("");
                        }
                      }}
                      margin="normal"
                      disabled={selected.length >= 3}
                    />
                    <Grid container spacing={1}>
                      <Grid size={updateText ? 11 : 10.5}>
                        <TextField
                          fullWidth
                          required={selected.length > 0 ? false : true}
                          name="buttontext"
                          label="Button Text"
                          value={multiButton}
                          onChange={(e) => {
                            if (e.target.value.length <= 20) {
                              setMultiButton(e.target.value);
                            }
                          }}
                          margin="normal"
                          disabled={!linkUrl || selected.length >= 3}
                        />
                        <InputLabel sx={{ fontSize: "12px" }}>
                          Button must be less than 20 characters (
                          {multiButton.length}/20)
                        </InputLabel>
                      </Grid>
                      <Grid size={updateText ? 1 : 1.5} sx={{ mt: 2.5 }}>
                        {updateText ? (
                          <button
                            type="button"
                            className="plus alt"
                            onClick={onClickMultiAddButton}
                            disabled={selected.length >= 3}>
                            +
                          </button>
                        ) : (
                          <Button type="button" onClick={onClickMultiAddButton}>
                            Update
                          </Button>
                        )}
                      </Grid>
                    </Grid>
                    <Box
                      className={classes.checkBoxStyles}
                      sx={{ mt: "1.5vh !important" }}>
                      {selected
                        .filter((value: any) => value.multiButton?.trim())
                        .map((value: any, i: any) => (
                          <Chip
                            key={i}
                            label={value.multiButton}
                            avatar={
                              <Avatar>
                                <EditIcon
                                  fontSize="small"
                                  onClick={() => handleEdit(i, value)}
                                />
                              </Avatar>
                            }
                            onDelete={() => handleDelete(i)}
                            deleteIcon={<DeleteIcon />}
                            variant="outlined"
                            color="primary"
                          />
                        ))}
                    </Box>
                  </Box>
                </Box>
              </DialogContent>
              <DialogActions sx={{ justifyContent: "space-between", p: 3 }}>
                <Button onClick={onClose} color="primary">
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    if (validateBeforeSend(values)) {
                      setConfirmOpen(true);
                      fetchConfirmationData();
                    }
                  }}>
                  {editMessage ? "Update" : "Schedule"}
                </Button>
              </DialogActions>
              <MessageConfirmDialog
                open={confirmOpen}
                onClose={() => {
                  setConfirmOpen(false);
                  setConfirmError(false);
                  setConfirmData(null);
                }}
                onConfirm={() => {
                  setConfirmOpen(false);
                  submitForm();
                }}
                loading={false}
                confirmData={confirmData}
                recipientCountError={confirmError}
                excludedCampaignsCount={excludedCampaigns.length}
              />
            </Form>
          )}
        </Formik>
      </Dialog>
    </>
  );
};
